import { Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges } from '@angular/core';

import Utils from '../Utils';

@Component({
    selector: 'cron-time-picker',
    templateUrl: './time-picker.component.html',
    styleUrls: ['./time-picker.component.css'],
    standalone: false,
})
export class TimePickerComponent implements OnInit, OnChanges {
    @Output() public change = new EventEmitter();
    @Input() public disabled: boolean;
    @Input() public time: any;
    @Input() public selectClass: string;
    @Input() public use24HourTime: boolean;
    @Input() public hideSeconds: boolean;
    @Input() public disablePreviousTimes: boolean;
    @Input() public dependentDate: Date;
    @Input() public timezone: string;

    public hours: { value: number; disabled: boolean }[];
    public minutes: { value: number; disabled: boolean }[];
    public seconds: { value: number; disabled: boolean }[];
    public hourTypes: string[];
    public disableHour = false;
    public disableMinute = false;
    public disableSecond = false;

    public ngOnInit() {
        this.updateTimeOptions();
    }

    public ngOnChanges(changes: SimpleChanges) {
        if (changes['dependentDate'] || changes['timezone']) {
            this.updateTimeOptions();
        }
    }

    private updateTimeOptions() {
        this.hours = (this.use24HourTime ? Utils.getRange(0, 23) : Utils.getRange(1, 12)).map(hour => ({ value: hour, disabled: false }));
        this.minutes = Utils.getRange(0, 59).map(minute => ({ value: minute, disabled: false }));
        this.seconds = Utils.getRange(0, 59).map(second => ({ value: second, disabled: false }));
        this.hourTypes = ['AM', 'PM'];
        this.disablePreviousTimes && this.updateTimeWithDate();
    }

    public updateTimeWithDate(): void {
        if (!this.dependentDate) return;

        if (this.isBeforeToday(this.dependentDate)) {
            this.disableAllTimes();
            return;
        }
        if (this.isToday(this.dependentDate)) {
            const now = this.getTimezoneNow(this.timezone);
            if (this.use24HourTime) {
                this.hours.forEach(hour => (hour.disabled = hour.value < now.hours));
                this.updateDisabledTimes();
                this.onHourChange();
            }
        }
    }

    public onHourChange(): void {
        if (!this.disablePreviousTimes || !this.dependentDate || !this.isToday(this.dependentDate)) return;

        const now = this.getTimezoneNow(this.timezone);
        const { hours: selectedHour } = this.time;
        this.minutes.forEach(minute => {
            minute.disabled = selectedHour === now.hours && minute.value < now.minutes;
        });
        this.updateDisabledTimes();
        this.onMinuteChange();
    }

    public onMinuteChange(): void {
        if (!this.disablePreviousTimes || !this.dependentDate || this.hideSeconds || !this.isToday(this.dependentDate)) return;

        const now = this.getTimezoneNow(this.timezone);
        const { hours: selectedHour, minutes: selectedMinute } = this.time;
        this.seconds.forEach(second => {
            const isPast =
                selectedHour < now.hours ||
                (selectedHour === now.hours && selectedMinute < now.minutes) ||
                (selectedHour === now.hours && selectedMinute === now.minutes && second.value < now.seconds);

            second.disabled = isPast;
        });
        this.updateDisabledTimes();
    }

    private disableAllTimes(): void {
        this.hours.forEach(hour => (hour.disabled = true));
        this.minutes.forEach(minute => (minute.disabled = true));
        !this.hideSeconds && this.seconds.forEach(second => (second.disabled = true));
        this.disableHour = true;
        this.disableMinute = true;
        this.disableSecond = true;
    }

    private isToday(date: Date): boolean {
        const now = this.getTimezoneNow(this.timezone);
        return date.getFullYear() === now.year && date.getMonth() === now.month - 1 && date.getDate() === now.day;
    }

    private isBeforeToday(date: Date): boolean {
        const now = this.getTimezoneNow(this.timezone);
        return (
            new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime() < new Date(now.year, now.month - 1, now.day).getTime()
        );
    }

    private getTimezoneNow(timeZone?: string): {
        year: number;
        month: number;
        day: number;
        hours: number;
        minutes: number;
        seconds: number;
    } {
        if (!timeZone) {
            const now = new Date();
            return {
                year: now.getFullYear(),
                month: now.getMonth() + 1,
                day: now.getDate(),
                hours: now.getHours(),
                minutes: now.getMinutes(),
                seconds: now.getSeconds(),
            };
        }
        try {
            const formatter = new Intl.DateTimeFormat('en-US', {
                timeZone,
                year: 'numeric',
                month: '2-digit',
                day: '2-digit',
                hour: '2-digit',
                minute: '2-digit',
                second: '2-digit',
                hour12: false,
            });
            const parts = formatter.formatToParts(new Date());
            const get = (type: string) => parseInt(parts.find(p => p.type === type).value, 10);
            return {
                year: get('year'),
                month: get('month'),
                day: get('day'),
                hours: get('hour'),
                minutes: get('minute'),
                seconds: get('second'),
            };
        } catch {
            const now = new Date();
            return {
                year: now.getFullYear(),
                month: now.getMonth() + 1,
                day: now.getDate(),
                hours: now.getHours(),
                minutes: now.getMinutes(),
                seconds: now.getSeconds(),
            };
        }
    }

    private updateDisabledTimes(): void {
        this.disableHour = false;
        this.disableMinute = this.hours.find(hour => hour.value === this.time.hours)?.disabled;
        this.disableSecond = this.disableMinute || this.minutes.find(minute => minute.value === this.time.minutes)?.disabled;
    }
}
